import { useMemo } from 'react';
import { Input, Select } from '@react-apps/design-system';
import type { ProviderAccount } from '@/modules/sales/domain/entities/sales.entity';
import { useProviderAccounts } from '@/modules/sales/presentation/hooks/use-provider-accounts';
import {
  CATALOG_PROVIDERS,
  type CatalogProvider,
  type ProductExternalKeyInput,
} from '../../domain/entities/catalog.entity';
import { CATALOG_PROVIDER_LABELS } from '../labels';

/**
 * As quatro partes da chave do entregável, como o operador as preenche. É um
 * formulário só porque a chave é uma identidade só: o back recusa meia chave
 * (`UpdateProductExternalKeyRequestDto` exige as quatro), e é o par
 * produto × oferta que decide o que a compra entrega — duas ofertas do mesmo
 * produto (6 meses / 6 meses + bônus) são entregáveis diferentes.
 *
 * Diferente do de-para do TOTVS, aqui **não existe conta curinga**: a chave é
 * escopada pela conta de gateway, e casar "qualquer conta" faria a mesma oferta
 * de duas contas entregar o mesmo produto sem que ninguém tenha decidido isso.
 */
export interface ProductExternalKeyFormValue {
  provider: CatalogProvider;
  providerAccountId: string;
  productExternalId: string;
  offerExternalId: string;
}

export const EMPTY_PRODUCT_EXTERNAL_KEY: ProductExternalKeyFormValue = {
  provider: 'guru',
  providerAccountId: '',
  productExternalId: '',
  offerExternalId: '',
};

const PROVIDER_OPTIONS = CATALOG_PROVIDERS.map((provider) => ({
  value: provider,
  label: CATALOG_PROVIDER_LABELS[provider],
}));

const NOTES_MAX_LENGTH = 40;

function getProviderAccountOptionLabel(account: ProviderAccount): string {
  if (!account.notes) return account.displayName;
  const notes =
    account.notes.length > NOTES_MAX_LENGTH
      ? `${account.notes.slice(0, NOTES_MAX_LENGTH - 1)}…`
      : account.notes;
  return `${account.displayName} — ${notes}`;
}

/** Recorta os espaços das partes digitadas; o `provider` já é lista fechada. */
export function trimProductExternalKey(
  value: ProductExternalKeyFormValue,
): ProductExternalKeyInput {
  return {
    provider: value.provider,
    providerAccountId: value.providerAccountId.trim(),
    productExternalId: value.productExternalId.trim(),
    offerExternalId: value.offerExternalId.trim(),
  };
}

export function isProductExternalKeyComplete(
  value: ProductExternalKeyFormValue,
): boolean {
  const key = trimProductExternalKey(value);
  return (
    key.providerAccountId.length > 0 &&
    key.productExternalId.length > 0 &&
    key.offerExternalId.length > 0
  );
}

export function productExternalKeyEquals(
  a: ProductExternalKeyFormValue,
  b: ProductExternalKeyFormValue,
): boolean {
  const left = trimProductExternalKey(a);
  const right = trimProductExternalKey(b);
  return (
    left.provider === right.provider &&
    left.providerAccountId === right.providerAccountId &&
    left.productExternalId === right.productExternalId &&
    left.offerExternalId === right.offerExternalId
  );
}

interface ProductExternalKeyFieldsProps {
  value: ProductExternalKeyFormValue;
  onChange: (value: ProductExternalKeyFormValue) => void;
  disabled?: boolean;
}

export function ProductExternalKeyFields({
  value,
  onChange,
  disabled,
}: ProductExternalKeyFieldsProps) {
  const { data: providerAccounts } = useProviderAccounts(value.provider);
  const providerAccountOptions = useMemo(
    () =>
      providerAccounts?.map((account) => ({
        value: account.id,
        label: getProviderAccountOptionLabel(account),
      })) ?? [],
    [providerAccounts],
  );

  return (
    <div className="space-y-4">
      <Select
        label="Gateway"
        options={PROVIDER_OPTIONS}
        value={value.provider}
        disabled={disabled}
        onChange={(e) =>
          // Trocar de gateway invalida a conta escolhida: a lista de contas é
          // por gateway, e manter o id antigo gravaria uma chave impossível.
          onChange({
            ...value,
            provider: e.target.value as CatalogProvider,
            providerAccountId: '',
          })
        }
        helperText="Onde a venda acontece. Indica também onde conferir os IDs abaixo."
      />

      <Select
        label="Conta do gateway"
        options={providerAccountOptions}
        placeholder="Selecione a conta…"
        value={value.providerAccountId}
        disabled={disabled}
        onChange={(e) =>
          onChange({ ...value, providerAccountId: e.target.value })
        }
        helperText="A chave é escopada pela conta — o mesmo produto/oferta vindo de outra conta é outro entregável."
      />

      <Input
        label="ID do produto"
        value={value.productExternalId}
        disabled={disabled}
        onChange={(e) =>
          onChange({ ...value, productExternalId: e.target.value })
        }
        required
        helperText="Identificador do produto-pai no gateway."
      />

      <Input
        label="ID da oferta"
        value={value.offerExternalId}
        disabled={disabled}
        onChange={(e) =>
          onChange({ ...value, offerExternalId: e.target.value })
        }
        required
        helperText="É a oferta que decide o que entra na entrega — duas ofertas do mesmo produto são produtos diferentes aqui."
      />
    </div>
  );
}
